import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { CohortTypeEnum } from 'src/portal/buyerscaddy-api/models/cohort-type-enum';
import { CompanyRefDto } from 'src/portal/buyerscaddy-api/models/company-ref-dto';
import { CohortService } from 'src/portal/buyerscaddy-api/services/cohort.service';
import { CompetitorPremiumApiService } from 'src/portal/owler-api/services/competitor-premium-api.service';

@Injectable({
  providedIn: 'root'
})
export class CohortDataService {

  public defaultCohortloaded: boolean = false
  public definedCohortloaded: boolean = false;
  public aspirationalCohortloaded: boolean = false;

  private pageSize = 100;


  constructor(private cohortService:CohortService,private competitorPremiumApiService:CompetitorPremiumApiService) { }

  public getDefaultCohort(): Observable<CompanyRefDto[]> {
    if (!this.cohortService) {
      throw new Error('cohortService is null');
    }
    this.defaultCohortloaded = true;
    return this.cohortService.getCohort({ cohort: CohortTypeEnum.DEFAULT,pageSize:this.pageSize}).pipe(
      finalize(() => this.defaultCohortloaded = false),
      catchError(err => throwError(() => err))
    );
  }

  public updateCohortDefault(body:CompanyRefDto[]){
    this.defaultCohortloaded = true;
    return this.cohortService.updateCohort({cohort:CohortTypeEnum.DEFAULT,body:body}).pipe(
      finalize(() => this.defaultCohortloaded = false),
      catchError(err => throwError(() => err))
    );
  }

  public removeCohortDefault(body:CompanyRefDto[]){
    this.defaultCohortloaded = true;
    return this.cohortService.deleteCohort({cohort:CohortTypeEnum.DEFAULT,body:body}).pipe(
      finalize(() => this.defaultCohortloaded = false),
      catchError(err => throwError(() => err))
    )
  }

  public getDefinedCohort(): Observable<CompanyRefDto[]> {
    if (!this.cohortService) {
      throw new Error('cohortService is null');
    }
    this.definedCohortloaded = true;
    return this.cohortService.getCohort({ cohort: CohortTypeEnum.DEFINED,pageSize:this.pageSize}).pipe(
      finalize(() => this.definedCohortloaded = false),
      catchError(err => throwError(() => err))
    );
  }

  public updateCohortDefined(body:CompanyRefDto[]){
    this.definedCohortloaded = true;
    return this.cohortService.updateCohort({cohort:CohortTypeEnum.DEFINED,body:body}).pipe(
      finalize(() => this.definedCohortloaded = false),
      catchError(err => throwError(() => err))
    );
  }
  public removeCohortDefined(body:CompanyRefDto[]){
    this.definedCohortloaded = true;
    return this.cohortService.deleteCohort({cohort:CohortTypeEnum.DEFINED,body:body}).pipe(
      finalize(() => this.definedCohortloaded = false),
      catchError(err => throwError(() => err))
    )
  }

  public getAspirationalCohort(): Observable<CompanyRefDto[]> {
    if (!this.cohortService) {
      throw new Error('cohortService is null');
    }
    this.aspirationalCohortloaded = true;
    return this.cohortService.getCohort({ cohort: CohortTypeEnum.ASPIRATIONAL,pageSize:this.pageSize}).pipe(
      finalize(() => this.aspirationalCohortloaded = false),
      catchError(err => throwError(() => err))
    );
  }

  public updateCohortAspirational(body:CompanyRefDto[]){
    this.aspirationalCohortloaded = true;
    return this.cohortService.updateCohort({cohort:CohortTypeEnum.ASPIRATIONAL,body:body}).pipe(
      finalize(() => this.aspirationalCohortloaded = false),
      catchError(err => throwError(() => err))
    )
  }

  public removeCohortAspirational(body:CompanyRefDto[]){
    this.aspirationalCohortloaded = true;
    return this.cohortService.deleteCohort({cohort:CohortTypeEnum.ASPIRATIONAL,body:body}).pipe(
      finalize(() => this.aspirationalCohortloaded = false),
      catchError(err => throwError(() => err)))
  }

  public setDefinedCompetotors(website) {
    const normalizedWebsite = this.normalizeWebsiteForLookup(website);
    if (!normalizedWebsite) {
      return of({ competitor: [] });
    }

    this.definedCohortloaded = true
   return this.competitorPremiumApiService.getCompetitorsForWebsite({website: normalizedWebsite})
      .pipe(
        map(res => ({
          competitor: (res?.competitor ?? [])
            .slice(0, 10)
            .map(x => x.website)
            .filter((website): website is string => !!website)
        })),
        catchError(error => {
          console.error('Ошибка:', error);
          return of({ competitor: [] });
        }),
        finalize(() => this.definedCohortloaded = false),
      );
  }

  public async ensureDefinedCohortReady(website?: string | null): Promise<boolean> {
    try {
      const currentDefined = await firstValueFrom(this.getDefinedCohortOrEmpty());
      if (currentDefined?.length > 0) {
        return true;
      }

      const normalizedWebsite = this.normalizeWebsiteForLookup(website);
      if (!normalizedWebsite) {
        return false;
      }

      const competitorResult = await firstValueFrom(this.setDefinedCompetotors(normalizedWebsite));
      const normalizedDomains = this.getUniqueDomains(
        this.extractDomainsFromUrls(competitorResult?.competitor ?? [])
      );

      if (normalizedDomains.length === 0) {
        return false;
      }

      await firstValueFrom(
        this.updateCohortDefined(normalizedDomains.map((domain) => ({ website: domain })))
      );

      const updatedDefined = await firstValueFrom(this.getDefinedCohortOrEmpty());
      return (updatedDefined?.length ?? 0) > 0;
    } catch (error) {
      console.error('Failed to ensure defined cohort readiness:', error);
      return false;
    }
  }

  public extractDomainsFromUrls(urls) {
    const domains = urls.map(url => {
      let domain = '';
      try {
        const parsedUrl = new URL(url);
        domain = parsedUrl.hostname.replace(/^www\./i, '');
      } catch (error) {
        // Handling URLs without protocol
        const domainWithoutProtocol = url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '');
        const index = domainWithoutProtocol.indexOf('/');
        domain = index !== -1 ? domainWithoutProtocol.substring(0, index) : domainWithoutProtocol;
        domain = domain.replace(/^www\./i, '');
      }
      // Exclude subdomains
      const domainParts = domain.split('.');
      if (domainParts.length > 2) {
        domain = domainParts.slice(-2).join('.');
      }

      // Removing unsuitable characters
      domain = domain.replace(/[^\w.-]/g, '');

      // Domain validity check
      const isValidDomain = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain);
      return isValidDomain ? domain : null;
    });

    return domains.filter(domain => domain !== null);
  }

  private getUniqueDomains(domains: string[]): string[] {
    return Array.from(new Set((domains ?? []).map((domain) => domain.toLowerCase())));
  }

  private getDefinedCohortOrEmpty(): Observable<CompanyRefDto[]> {
    return this.getDefinedCohort().pipe(
      catchError((err) => {
        if (err?.status === 400) {
          return of([]);
        }

        return throwError(() => err);
      })
    );
  }

  private normalizeWebsiteForLookup(website?: string | null): string | null {
    const rawWebsite = (website || '').trim();
    if (!rawWebsite) {
      return null;
    }

    let hostname = '';
    try {
      hostname = new URL(rawWebsite).hostname;
    } catch (error) {
      hostname = rawWebsite.replace(/^(?:https?:\/\/)?/i, '').split('/')[0];
    }

    hostname = hostname.replace(/^www\./i, '').trim().toLowerCase();
    hostname = hostname.replace(/[^\w.-]/g, '');

    const isValidDomain = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(hostname);
    return isValidDomain ? hostname : null;
  }
}
